<template>
  <div id="dataPointDetails">
    <!-- Data Point Details -->
    <b-card
      id="dataPointDetailsCard"
      class="blockDrawer large"
      :class="$store.state.drawer.dataPointDetails.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="hideDataPointDetails()"
        >
          <i class="far fa-times"></i>
        </a>
        <!-- Data Point Title -->
        <h4>Data Point</h4>
        <div v-if="$store.state.dataset.dynamic">
          <!-- Data Point Edit Button -->
          <b-btn
            v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor' || ($store.state.collector.editors.includes($store.state.currentUser.uid))"
            @click="editDataPoint()"
            class="mb-2"
          >
            <i class="fas fa-pencil-alt"></i> Edit Data Point
          </b-btn>
          <!-- REVISIONS -->
          <b-btn
            v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor'"
            @click="viewRevisions()"
            class="mb-2 ml-1"
          >
            <i class="fas fa-code-commit"></i> Revisions
          </b-btn>
        </div><!-- if dynamic dataset -->
      </template>
      <div v-if="$store.state.dataPoint.coordinates">
        <p><strong>Coordinates:</strong> {{ $store.state.dataPoint.coordinates[0] }}, {{ $store.state.dataPoint.coordinates[1] }}</p>
      </div>
      <div v-if="$store.state.dataset.dynamic">
        <div v-for="(groupedFields, group) in groupedBlockFields" v-bind:key="group">
          <h5 class="text-underline" v-if="group !== 'Others'">{{ group }}</h5> <!-- Group Title -->

          <!-- FIELDS -->
          <div
            v-for="(field, i) in groupedFields"
            v-bind:key="i"
          >
            <!-- TEXT FIELDS -->
            <p
              v-if="field.type !== 'Image' && field.type !== 'Date and Time' && field.type !== 'Computer Vision' && !field.isLogEnabled"
            ><strong>{{ field.label }}:</strong> {{ $store.state.dataPoint[field.machine_name] }}</p>
            <div v-if="field.isLogEnabled" class="mb-2">
              <p class="mb-1">
                <strong>{{ field.label }}:</strong> {{ getLatestLogDisplay(field) }} <span v-if="field.suffix">{{ field.suffix }}</span>
                <b-button size="sm" variant="outline-secondary" class="ml-2" @click="openLogHistory(field)">View logs</b-button>
              </p>
            </div>
            <!-- DATE AND TIME FIELDS -->
            <div v-if="field.type === 'Date and Time' && $store.state.dataPoint[field.machine_name] && !field.isLogEnabled">
              <p
                v-if="field.type === 'Date and Time' && field.includeTime && $store.state.dataPoint[field.machine_name]"
              ><strong>{{ field.label }}:</strong> {{ $store.state.dataPoint[field.machine_name] | dateTime }}</p>
              <p
                v-if="field.type === 'Date and Time' && !field.includeTime && $store.state.dataPoint[field.machine_name]"
              ><strong>{{ field.label }}:</strong> {{ $store.state.dataPoint[field.machine_name] | dateTimeDate }}</p>
            </div>
            <!-- IMAGE GALLERY -->
            <p
              v-if="field.type === 'Image' && slideshowsReady && !field.isLogEnabled"
            ><strong>{{ field.label }}:</strong> Image(s)</p>
            <b-carousel
              v-if="field.type === 'Image' && slideshowsReady"
              :id="field.machine_name"
              controls
              indicators
              class="mb-2"
            >
              <b-carousel-slide
                v-for="(image, i) in slideshows[field.machine_name].slides"
                v-bind:key="i"
                :img-src="image.url"
              >
                <div v-if="image.caption" class="image-caption-overlay text-center small">
                  {{ image.caption }}
                </div>
              </b-carousel-slide>
            </b-carousel>

            <!-- Fullscreen viewer for non-logged image headers (matches log history viewer style) -->
            <b-btn
              v-if="field.type === 'Image' && !field.isLogEnabled && slideshowsReady"
              size="sm"
              variant="outline-secondary"
              class="mb-3"
              @click="openFullscreen(field)"
            >
              View full screen
            </b-btn>

            <!-- COMPUTER VISION PREVIEW -->
            <p
              v-if="field.type === 'Computer Vision' && slideshowsReady && !fullscreenSlides"
              class="mb-0"
            ><strong><i class="fas fa-robot mr-1"></i>{{ field.label }}:</strong></p>
            <div id="cvSection" v-if="field.type === 'Computer Vision' && slideshowsReady && !fullscreenSlides">
              <div
                v-for="(image, i) in slideshows[field.machine_name].slides"
                v-bind:key="i"
                class="preview-image-container"
                id="cvContainer"
              >
                <img :src="image.url" width="100%" id="cvImg">
              </div><!-- /.preview-image-container -->
            </div><!-- /#cvSection -->

          </div><!-- /FIELDS -->
        </div><!-- /GROUPS -->
        <p v-if="$store.state.dataPoint.updatedOn != undefined"><strong>Updated:</strong> {{ $store.state.dataPoint.updatedOn | dateTime }}</p>
        <p v-if="$store.state.dataPoint.createdOn != undefined"><strong>Created:</strong> {{ $store.state.dataPoint.createdOn | dateTime }}</p>
        <p><strong>Lat/lng:</strong> {{ centroidLat }}, {{ centroidLng }}</p>
        <p v-if="polygonAreaSqM != null"><strong>Area:</strong> <span v-if="$store.state.userProfile.measurementSystem === 'Metric'">{{ polygonAreaSqM | toHectares }} hectares</span><span v-else>{{ polygonAreaSqM | toAcres }} acres</span></p>
        <p><strong>ID:</strong> {{ $store.state.dataPoint.id }}</p>
      </div><!-- if dynamic -->
      <div v-else>
        <p
          v-for="(field, i) in $store.state.dataPoint.fields"
          v-bind:key="i"
        ><strong>{{ field }}:</strong> {{ $store.state.dataPoint[field] }}</p>
      </div><!-- if dynamic -->
    </b-card>

    <LogHistoryModal
      :show="showLogModal"
      entity-type="datapoint"
      :entity-id="$store.state.dataPoint && $store.state.dataPoint.id"
      :collector-id="$store.state.collector && $store.state.collector.id"
      :farm-id="($store.state.collector && $store.state.collector.farmId) || ($store.state.farm && $store.state.farm.id)"
      :header="logHistoryHeader"
      :can-edit="$store.state.role === 'Administrator' || $store.state.role === 'Editor' || ($store.state.collector && $store.state.collector.editors && $store.state.collector.editors.includes($store.state.currentUser && $store.state.currentUser.uid))"
      @updated="onLogUpdated"
      @close="showLogModal = false"
    />

    <!-- Shared fullscreen image viewer for standard (non-logged) headers -->
    <b-modal
      v-model="fullscreenSlides"
      size="xl"
      body-class="p-0"
      content-class="log-image-viewer-modal"
      hide-header
      hide-footer
      @hidden="fullscreenIndex = 0"
    >
      <div class="d-flex flex-column h-100" style="min-height: 70vh;">
        <div class="d-flex align-items-center justify-content-between p-2 border-bottom bg-light">
          <span class="small text-muted">
            <span v-if="fullscreenFieldLabel">{{ fullscreenFieldLabel }} — </span>
            Image {{ fullscreenIndex + 1 }} of {{ fullscreenImages.length }}
          </span>
          <div>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              :disabled="isFirstFullscreenImage"
              @click="prevFullscreenImage"
            >
              <i class="fas fa-chevron-left"></i> Previous
            </b-button>
            <b-button
              size="sm"
              variant="outline-primary"
              class="mr-1"
              :disabled="isLastFullscreenImage"
              @click="nextFullscreenImage"
            >
              Next <i class="fas fa-chevron-right"></i>
            </b-button>
            <b-button
              size="sm"
              variant="success"
              class="mr-1"
              :disabled="!currentFullscreenImageUrl"
              @click="downloadFullscreenImage"
            >
              <i class="fas fa-download"></i> Download
            </b-button>
            <b-button size="sm" variant="secondary" @click="fullscreenSlides = false">Close</b-button>
          </div>
        </div>
        <div class="flex-grow-1 d-flex align-items-center justify-content-center p-3 bg-dark overflow-auto">
          <div class="text-center">
            <img
              v-if="currentFullscreenImageUrl"
              :src="currentFullscreenImageUrl"
              class="log-viewer-img"
              alt="Image"
            />
            <span v-else class="text-white">Loading image…</span>
            <div v-if="currentFullscreenCaption" class="mt-2 small text-light">
              {{ currentFullscreenCaption }}
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div><!-- /.dataPointDetails -->
</template>

<script>
import moment from 'moment'
import firebase from 'firebase/compat/app'
import turf from '@turf/turf'
import { getDatapointLogHistory } from '../../services/HeaderLogService'
import LogHistoryModal from '../LogHistoryModal'
const fb = require('../../firebaseConfig.js')

export default {
  components: { LogHistoryModal },
  data () {
    return {
      slideshows: {},
      slideshowsReady: false,
      fullscreenSlides: false,
      fullscreenImages: [],
      fullscreenIndex: 0,
      fullscreenFieldLabel: '',
      logHistory: {},
      showLogModal: false,
      logHistoryHeader: null
    }
  },
  created () {
    this.formatFields()
    this.loadLogHistory()
  },
  computed: {
    groupedBlockFields () {
      return this.$store.state.collector.fields.reduce((grouped, field) => {
        const group = field.group || 'Others'
        if (!grouped[group]) {
          grouped[group] = []
        }
        grouped[group].push(field)
        return grouped
      }, {})
    },
    centroidLat () {
      const dp = this.$store.state.dataPoint
      if (!dp) return ''
      const g = dp.geolocation
      const lat = g && (g.latitude ?? g._latitude)
      if (typeof lat === 'number') return lat.toFixed(6)
      return this.representativeGeometryLat
    },
    centroidLng () {
      const dp = this.$store.state.dataPoint
      if (!dp) return ''
      const g = dp.geolocation
      const lng = g && (g.longitude ?? g._longitude)
      if (typeof lng === 'number') return lng.toFixed(6)
      return this.representativeGeometryLng
    },
    parsedGeometry () {
      const dp = this.$store.state.dataPoint
      if (!dp || !dp.geometry) return null
      try {
        return typeof dp.geometry === 'string' ? JSON.parse(dp.geometry) : dp.geometry
      } catch (e) {
        return null
      }
    },
    representativeGeometryPoint () {
      const geom = this.parsedGeometry
      if (!geom) return null
      try {
        if (geom.type === 'Point' && Array.isArray(geom.coordinates)) {
          return { lat: geom.coordinates[1], lng: geom.coordinates[0] }
        }
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          const feature = { type: 'Feature', properties: {}, geometry: geom }
          const lengthKm = turf.length(feature, { units: 'kilometers' })
          const midpoint = turf.along(feature, lengthKm / 2, { units: 'kilometers' })
          return { lat: midpoint.geometry.coordinates[1], lng: midpoint.geometry.coordinates[0] }
        }
        if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
          const c = turf.centroid({ type: 'Feature', properties: {}, geometry: geom })
          return { lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0] }
        }
      } catch (e) { /* ignore */ }
      return null
    },
    representativeGeometryLat () {
      const p = this.representativeGeometryPoint
      return p && typeof p.lat === 'number' ? p.lat.toFixed(6) : ''
    },
    representativeGeometryLng () {
      const p = this.representativeGeometryPoint
      return p && typeof p.lng === 'number' ? p.lng.toFixed(6) : ''
    },
    polygonAreaSqM () {
      const geom = this.parsedGeometry
      if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return null
      try {
        return turf.area({ type: 'Feature', properties: {}, geometry: geom })
      } catch (e) {
        return null
      }
    },
    currentFullscreenImage () {
      if (!this.fullscreenImages || !this.fullscreenImages.length) return null
      const idx = Math.min(Math.max(this.fullscreenIndex, 0), this.fullscreenImages.length - 1)
      return this.fullscreenImages[idx]
    },
    currentFullscreenImageUrl () {
      const img = this.currentFullscreenImage
      return img && img.url ? img.url : null
    },
    currentFullscreenCaption () {
      const img = this.currentFullscreenImage
      return img && img.caption ? img.caption : ''
    },
    isFirstFullscreenImage () {
      return this.fullscreenIndex <= 0
    },
    isLastFullscreenImage () {
      return !this.fullscreenImages || this.fullscreenIndex >= this.fullscreenImages.length - 1
    }
  },
  methods: {
    viewRevisions () {
      this.$store.commit('hideDrawer', 'dataPointDetails')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'revisions')
      }, 300)
    },
    openFullscreen (field) {
      if (!field || !field.machine_name || !this.slideshows[field.machine_name]) return
      const slides = this.slideshows[field.machine_name].slides || []
      this.fullscreenImages = slides
      this.fullscreenIndex = 0
      this.fullscreenFieldLabel = field.label || ''
      this.fullscreenSlides = true
    },
    prevFullscreenImage () {
      if (this.isFirstFullscreenImage) return
      this.fullscreenIndex -= 1
    },
    nextFullscreenImage () {
      if (this.isLastFullscreenImage) return
      this.fullscreenIndex += 1
    },
    downloadFullscreenImage () {
      const img = this.currentFullscreenImage
      if (!img) return
      this.downloadImage(img)
    },
    downloadImage (image) {
      let path = null

      if (image && (image.large || image.path)) {
        path = image.large || image.path
      } else if (typeof image === 'string') {
        path = image
      }

      if (!path || typeof path !== 'string') return

      firebase.storage().ref(path).getDownloadURL().then(async url => {
        const filename = this.$options.filters && this.$options.filters.shortenPath
          ? (this.$options.filters.shortenPath(path) || 'image.jpg')
          : 'image.jpg'
        try {
          const res = await fetch(url, { mode: 'cors' })
          const blob = await res.blob()
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = filename
          a.rel = 'noopener'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(a.href)
        } catch (e) {
          window.open(url, '_blank', 'noopener')
        }
      }).catch(err => {
        console.error('Download failed:', err)
      })
    },
    getImagesForField (field) {
      if (!this.$store.state.dataPoint || !field || !field.machine_name) return []
      const raw = this.$store.state.dataPoint[field.machine_name]
      if (!raw) return []
      if (Array.isArray(raw)) {
        return raw.filter(img => img && (img.path || img.large))
      }
      if (raw && (raw.path || raw.large)) return [raw]
      return []
    },
    getImageUrls () {
      if (!this.$store.state.collector.fields || !this.$store.state.dataPoint) return
      this.$store.state.collector.fields.forEach(field => {
        if (field.type !== 'Image') return
        const raw = this.$store.state.dataPoint[field.machine_name]
        if (!raw) return
        const images = Array.isArray(raw) ? raw : [raw]
        this.slideshows[field.machine_name] = {
          slides: [],
          show: false
        }
        let completed = 0
        images.forEach((image, index) => {
          const path = image.large || image.path
          if (!path) return
          firebase.storage().ref(path).getDownloadURL().then(url => {
            this.$set(this.slideshows[field.machine_name].slides, index, {
              path: image.path || path,
              url: url,
              caption: image.caption
            })
            completed++
            if (completed === images.length) {
              this.slideshows[field.machine_name].show = true
              let readyToShowSlideshows = true
              this.$store.state.collector.fields.forEach(f => {
                if (f.type === 'Image' && !this.slideshows[f.machine_name].show) {
                  readyToShowSlideshows = false
                }
              })
              this.slideshowsReady = readyToShowSlideshows
            }
          })
        })
      })
    },
    getImageUrl (image, field) {
      const path = image.large || image.path
      if (!path) return Promise.resolve(false)
      return firebase.storage().ref(path).getDownloadURL().then(url => {
        this.slideshows[field.machine_name].slides.push({
          path: image.path,
          url: url,
          caption: image.caption
        })
        return true
      }).catch(err => {
        console.log(err)
        return false
      })
    },
    drawPredictions (response, scaleX, scaleY) {
      this.classes = []
      this.classObjs = {}

      const container = document.getElementById('cvContainer')

      // SETUP LABELS AND COLORS
      const colors = ['red', 'yellow', 'orange', 'blue']
      const classLabels = []
      response.predictions.forEach(prediction => {
        if (!classLabels.includes(prediction.class)) {
          classLabels.push(prediction.class)
        }
      })
      classLabels.forEach((label, i) => {
        this.classes.push({
          label: label,
          color: colors[i]
        })
        this.classObjs[label] = {
          color: colors[i]
        }
      })

      response.predictions.forEach(prediction => {
        const div = document.createElement('div')
        div.className = 'prediction'
        div.style.width = `${prediction.width * scaleX}px`
        div.style.height = `${prediction.height * scaleY}px`
        div.style.left = `${prediction.x * scaleX}px`
        div.style.top = `${prediction.y * scaleY}px`
        div.style.borderColor = this.classObjs[prediction.class].color

        const text = document.createTextNode(`${prediction.confidence.toFixed(2)}`)
        div.appendChild(text)

        container.appendChild(div)
      })
    },
    formatFields () {
      this.$store.state.collector.fields.forEach(field => {
        if (this.$store.state.dataPoint[field.machine_name] && (field.type === 'Image' || field.type === 'Computer Vision')) {
          this.slideshows[field.machine_name] = {
            slides: [],
            show: false
          }
          const raw = this.$store.state.dataPoint[field.machine_name]
          const images = Array.isArray(raw) ? raw : (raw && (raw.path || raw.large) ? [raw] : [])
          const imagePromises = []
          images.forEach(image => {
            const prom = this.getImageUrl(image, field)
            imagePromises.push(prom)
          })
          Promise.all(imagePromises).then(() => {
            this.slideshowsReady = true
          })
        } else if (this.$store.state.dataPoint[field.machine_name] && field.type === 'Formatted Text') {
          const content = this.$store.state.dataPoint[field.machine_name]
          const fixParenthesis = content.replace(/�s/g, '\'s')
          const removeSpecial = fixParenthesis.replace(/�/g, '"')
          this.$store.state.dataPoint[field.machine_name] = removeSpecial
        }
        if (this.$store.state.dataPoint[field.machine_name] && field.type === 'Computer Vision') {
          setTimeout(() => {
            const img = document.getElementById('cvImg')
            if (!img) return
            const displayWidth = img.clientWidth
            const displayHeight = img.clientHeight
            const scaleX = displayWidth / img.naturalWidth
            const scaleY = displayHeight / img.naturalHeight
            const dataPointCVData = this.$store.state.dataPoint[field.machine_name]
            const first = Array.isArray(dataPointCVData) ? dataPointCVData[0] : dataPointCVData
            if (first && first.cv) this.drawPredictions(first.cv, scaleX, scaleY)
          }, 1000)
        }
      })
    },
    async loadLogHistory () {
      if (!this.$store.state.collector || !this.$store.state.dataPoint || !this.$store.state.dataPoint.id) return
      const logHeaders = (this.$store.state.collector.fields || []).filter(field => field.isLogEnabled)
      await Promise.all(logHeaders.map(async field => {
        const { entries } = await getDatapointLogHistory({
          collectorId: this.$store.state.collector.id,
          dataPointId: this.$store.state.dataPoint.id,
          headerMachineName: field.machine_name,
          limit: 10
        })
        this.$set(this.logHistory, field.machine_name, entries)
      }))
    },
    getHeaderHistory (field) {
      return this.logHistory[field.machine_name] || []
    },
    getLatestLogDisplay (field) {
      if (!field || !field.machine_name) return '—'
      const latest = this.$store.state.dataPoint?.latestLogs && this.$store.state.dataPoint.latestLogs[field.machine_name]
      if (!latest || (latest.value === undefined || latest.value === null)) return '—'
      const v = latest.value
      if ((field.type === 'Date and Time' || field.logValueType === 'datetime') && v && typeof v.toDate === 'function') {
        const d = v.toDate()
        return field.includeTime ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : d.toLocaleDateString(undefined, { dateStyle: 'short' })
      }
      if ((field.type === 'Image' || field.logValueType === 'image') && v && typeof v === 'object') return 'Image(s)'
      return v
    },
    openLogHistory (field) {
      this.logHistoryHeader = field
      this.showLogModal = true
    },
    async onLogUpdated () {
      await this.loadLogHistory()
      if (this.$store.state.collector && this.$store.state.dataPoint && this.$store.state.dataPoint.id) {
        const doc = await fb.collectorsCollection.doc(this.$store.state.collector.id).collection('dataPoints').doc(this.$store.state.dataPoint.id).get()
        if (doc.exists) {
          const dataPointData = { ...doc.data(), id: doc.id }
          this.$store.commit('setDataPoint', dataPointData)
          this.$store.dispatch('refreshDatasetGeoJsonFromDatapoint', { dataPointId: doc.id, dataPointData })
          // Rebuild image slideshows so the drawer shows the new image
          this.slideshowsReady = false
          this.formatFields()
        }
        if (this.$store.state.collector.id) {
          await fb.collectorsCollection.doc(this.$store.state.collector.id).update({ reCompile: true })
        }
      }
    },
    formatLogHistoryEntry (entry) {
      const value = entry && entry.value !== undefined ? entry.value : '-'
      const note = entry && entry.notes ? ` (${entry.notes})` : ''
      if (!entry || !entry.timestamp || !entry.timestamp.seconds) return `${value}${note}`
      const date = entry.timestamp.toDate()
      return `${moment(date).format('MMM D, YYYY h:mm a')}: ${value}${note}`
    },
    editDataPoint () {
      // Set edit mode true
      this.$store.commit('setEditMode', true)
      this.$store.commit('hideDrawer', 'dataPointDetails')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'collector')
      }, 300)
    },
    hideDataPointDetails () {
      if (this.$store.state.mapObject.getLayer('selectedDataPoint')) {
        this.$store.state.mapObject.removeLayer('selectedDataPoint')
      }
      if (this.$store.state.mapObject.getLayer('selectedDataPointHighlight')) {
        this.$store.state.mapObject.removeLayer('selectedDataPointHighlight')
      }
      if (this.$store.state.mapObject.getLayer('selectedDataPointPolygon')) {
        this.$store.state.mapObject.removeLayer('selectedDataPointPolygon')
      }
      if (this.$store.state.mapObject.getLayer('selectedDataPointLine')) {
        this.$store.state.mapObject.removeLayer('selectedDataPointLine')
      }
      if (this.$store.state.mapObject.getSource('selectedDataPoint')) {
        this.$store.state.mapObject.removeSource('selectedDataPoint')
      }
      this.$store.commit('hideDrawer', 'dataPointDetails')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'datasetDetails')
      }, 300)
    }
  },
  filters: {
    toAcres (val) {
      if (val == null) return ''
      const acres = val * 0.000247105
      return acres.toFixed(2)
    },
    toHectares (val) {
      if (val == null) return ''
      const hectares = val / 10000
      return hectares.toFixed(2)
    },
    dateTime (val) {
      if (!val || !val.seconds) { return '-' }
      const date = val.toDate()
      return moment(date).fromNow()
      // return moment(date).format('MMMM Do YYYY, h:mm a')
    },
    dateTimeDate (val) {
      if (!val || !val.seconds) { return '-' }
      const date = val.toDate()
      // return moment(date).fromNow()
      return moment(date).format('MMMM Do, YYYY')
    },
    shortenPath (val) {
      if (!val || typeof val !== 'string') return ''
      const idx = val.lastIndexOf('/')
      return idx >= 0 ? val.substring(idx + 1) : val
    }
  }
}
</script>

<style>
.log-viewer-img {
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
}

#cvSection {
  position: relative;
  padding: 10px;
  background-color: #333;
  border-radius: 4px;
}
.image-caption-overlay {
  display: inline-block;
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
}

.fullscreenSlideshow {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 16px;
  z-index: 1050;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.fullscreenSlideshow .carousel {
  max-height: 80vh;
}

.fullscreen-slide-inner {
  min-height: 60vh;
}

.fullscreenSlideshow .carousel-item img {
  max-height: 70vh;
  width: auto;
  object-fit: contain;
}

.closeButton {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 11;
}

.fullscreenButton {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 11;
}

.carousel-indicators li {
  background-color: #eee;
}

@media (max-width: 600px) {
  .downloadImageBtn {
    display: none !important;
  }
}
</style>
